// ─────────────────────────────────────────────────────────────────────────────
// WHICH STAFF ROLES THE SERVER LETS NEAR BORROWER DATA — MIRRORED.
//
// ⚠️ KEEP IN LOCKSTEP WITH backend/constants/staffRoles.js. The frontend cannot
// import from backend/, so these exist twice by necessity;
// backend/test/staffRoleAccessParity.test.js fails if the two ever disagree.
// Same arrangement as priceBounds.js and downPaymentBands.js, and for the same
// reason.
//
// 🚨 WHY MIRROR IT RATHER THAN HAND-WRITE THE NAV RULES. The sidebar's borrower
// sections carried `hideForRoles: ['partner_success']` — correct, and written
// by hand on the day partner_success was added. `engineering` was added later,
// is deliberately absent from BORROWER_DATA_ROLES for the same reason, and
// nobody came back to the nav: so an engineering user was offered Borrowers,
// Loan Tape, Historicals and every borrower profile, and the server correctly
// refused all of it. The same dead affordance Partner Success was reporting,
// one role along, waiting to be reported again.
//
// 🔑 DERIVING THE NAV RULE FROM THIS LIST IS WHAT MAKES THAT UNREPRESENTABLE.
// A role added to STAFF_ROLES and not to BORROWER_DATA_ROLES is default-closed
// on the server AND hidden from the borrower nav, in one edit, without anybody
// remembering the second half.
// ─────────────────────────────────────────────────────────────────────────────

/** backend/constants/staffRoles.js STAFF_ROLES. */
export const STAFF_ROLES = [
  'admin',
  'account_manager',
  'contract_desk',
  'loan_officer',
  'client_success',
  'partner_success',
  'engineering',
];

/** backend/constants/staffRoles.js BORROWER_DATA_ROLES. */
export const BORROWER_DATA_ROLES = [
  'admin',
  'account_manager',
  'contract_desk',
  'loan_officer',
  'client_success',
];

/**
 * 🔑 THE NAV RULE, DERIVED. Every role the server will not give borrower data
 * to — so a section built on borrower data can hide exactly them without
 * naming anybody.
 */
export const NO_BORROWER_DATA = STAFF_ROLES.filter((r) => !BORROWER_DATA_ROLES.includes(r));

/** backend/constants/staffRoles.js SCOPED_BORROWER_READ_ROLES. */
export const SCOPED_BORROWER_READ_ROLES = ['partner_success'];

/**
 * 🚨 THE ROLES THAT GET *NOTHING* ON THE BORROWER SIDE — the complement of both
 * tiers, not of the first one.
 *
 * NO_BORROWER_DATA is still the right rule for a page that reads the full
 * borrower file, and partner_success is in it. But the Borrower SECTION now has
 * two pages that role may read, so the section gate cannot be NO_BORROWER_DATA
 * any more: hiding the section would hide the two pages it is allowed.
 *
 * 🔑 SO THE SECTION HIDES THIS, AND EVERY ITEM INSIDE IT KEEPS
 * NO_BORROWER_DATA. The two allowed items carry neither, and everything else in
 * the section is hidden from partner_success by the item rule it already had.
 * One list for "may see the section at all", one for "may see this page" —
 * which is exactly the distinction the grant makes.
 */
export const NO_BORROWER_SECTION = STAFF_ROLES.filter(
  (r) => !BORROWER_DATA_ROLES.includes(r) && !SCOPED_BORROWER_READ_ROLES.includes(r)
);

/**
 * ⚠️ AND THE TWO TIERS MUST NOT OVERLAP — the same assertion the server makes at
 * load, for the same reason: in borrowerDataGuard the full grant is checked
 * first, so a role in both would reach the scoped routes UNSCOPED.
 */
const overlap = SCOPED_BORROWER_READ_ROLES.filter((r) => BORROWER_DATA_ROLES.includes(r));
if (overlap.length) {
  throw new Error(
    `staffRoleAccess.js: ${overlap.join(', ')} is in both BORROWER_DATA_ROLES and `
    + 'SCOPED_BORROWER_READ_ROLES. The server refuses to load in this state; so does the client.'
  );
}

/**
 * 🚨 IS THE BORROWER SIDE READ-ONLY FOR THIS ROLE?
 *
 * Jesse, granting partner_success the Leads page and the Borrower Pipeline:
 * "No edit access."
 *
 * 🔑 THE SERVER ALREADY REFUSES EVERY WRITE — SCOPED_BORROWER_READ_ROUTES is
 * GET-only, so the archive, notes and email POSTs on the Leads router fall
 * through to the blanket denial. This is not the permission; it is the
 * AFFORDANCE. A button that 403s is the dead-affordance shape the whole nav
 * module exists to remove, and it is how this role's problems have been
 * reported every time: a page that loads and then refuses.
 *
 * ⚠️ SO IT HIDES, IT DOES NOT DISABLE. A disabled button still says "you could
 * do this if something were different", which is a question nobody can answer
 * from the screen.
 */
export function isBorrowerViewReadOnly(role) {
  return SCOPED_BORROWER_READ_ROLES.includes(role);
}

/**
 * The staff role on this browser, read the one way the app reads it.
 *
 * ⚠️ DERIVED HERE RATHER THAN IN EACH PAGE. App.js's StaffPageGuard already
 * parses this key; a second hand-rolled JSON.parse in every page that needs the
 * role is how two of them end up disagreeing about an unparseable value.
 */
export function currentStaffRole() {
  try {
    const u = JSON.parse(localStorage.getItem('doorly_admin_user') || 'null');
    return u && u.role ? u.role : null;
  } catch (_) {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 🚨 A BORROWER TICKET IS NOW VISIBLE TO A ROLE THAT CANNOT WRITE TO IT.
//
// Jesse: "I want Nick to be able to see a ticket for Cathy and be cc'd on the
// alert emails as well as email responses sent directly in the ticket."
//
// 🔑 SEEING IS NOT SENDING, so the backend widened the GET routes and left
// POST /tickets/:id/reply and POST /tickets/:id/transfer on the old narrow rule
// (backend/utils/ticketVisibility.js says why). This is the UI's half of that
// same split: without it the rep opens Cathy's ticket, finds a Reply box, writes
// an answer to a borrower, presses send and gets a 403 — work typed and lost, on
// a control that should never have been offered.
//
// ⚠️ PER TICKET, NOT PER ROLE. partner_success writes to PARTNER tickets all day
// — that is the desk. Only the borrower-subject ones are read-only for them, so
// the ticket's own user_type is half the question and gating on role alone would
// break the role's actual job.
//
// ⚠️ AND IT FAILS TO READ-ONLY WHEN THE SUBJECT IS UNKNOWN, matching the
// backend's fail-closed IN. A missing user_type offering a send button is the
// direction that costs somebody their typed reply.
//
// ⚠️ A HAND-KEPT MIRROR OF backend/constants/staffRoles.js, like
// BORROWER_DATA_ROLES above it — the frontend cannot import from backend/. Named
// rather than inlined so a test can compare the two lists instead of trusting
// that somebody updated both.
export const NON_BORROWER_TICKET_TYPES = ['partner', 'external', 'staff'];

export function isTicketReadOnly(role, ticketUserType) {
  if (!SCOPED_BORROWER_READ_ROLES.includes(String(role || ''))) return false;
  return !NON_BORROWER_TICKET_TYPES.includes(String(ticketUserType || ''));
}
